"use client";

import { Button, Typography } from "@repo/ui";
import { ChevronsDown } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";

interface ReadMoreProps {
  text?: string;
  minHeight: string; // Use string for CSS height values like '100px' or '5em'
  buttonText?: string;
}

const ReadMore: React.FC<ReadMoreProps> = ({
  text,
  minHeight,
  buttonText = "Read More",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Check if content overflows on mount and when text changes
  useEffect(() => {
    if (textRef.current) {
      // Temporarily remove height limit to measure actual height
      const originalMaxHeight = textRef.current.style.maxHeight;
      textRef.current.style.maxHeight = "";

      const actualHeight = textRef.current.scrollHeight;
      const minHeightValue = parseInt(minHeight, 10); // Assuming minHeight is in px for comparison

      // Restore original style
      textRef.current.style.maxHeight = originalMaxHeight;

      // Check if actual height exceeds minHeight
      if (actualHeight > minHeightValue) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }
  }, [text, minHeight]); // Re-run effect if text or minHeight changes

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      <div
        ref={textRef}
        style={{
          maxHeight: isExpanded ? "none" : minHeight,
          overflow: "hidden",
          maskImage:
            isOverflowing && !isExpanded
              ? "linear-gradient(to bottom, black 50%, transparent 100%)"
              : "none",
          WebkitMaskImage:
            isOverflowing && !isExpanded
              ? "linear-gradient(to bottom, black 50%, transparent 100%)"
              : "none",
        }}
      >
        <Typography className="[&_p]:mb-4">{text}</Typography>
      </div>
      <div className="text-center">
        {isOverflowing && !isExpanded && (
          <Button
            color="primary"
            variant="text"
            onClick={handleToggle}
            endIcon={<ChevronsDown size={20} />}
          >
            {buttonText}
          </Button>
        )}
        {isOverflowing && isExpanded && (
          <Button
            color="primary"
            variant="text"
            onClick={handleToggle}
            endIcon={<ChevronsDown size={20} className="rotate-180" />}
          >
            Read Less
          </Button>
        )}
      </div>
    </div>
  );
};

export default ReadMore;
